<!--
	Presentational support form. Renders the Halo embed iframe with a loading
	mask (and, if loading is slow, a non-blocking portal hint), or — only on a
	genuine load failure — an error state with a fallback link. All load/retry
	state is owned by the SupportFormModal container.
-->
<script lang="ts">
	import AbleyButton from './AbleyButton.svelte';
	import LoadingSpinner from './LoadingSpinner.svelte';

	interface Props {
		status: 'loading' | 'ready' | 'error';
		src: string;
		/** Content height (px) the iframe reported; null until it does. */
		height?: number | null;
		/** Show a non-blocking "open the portal" hint while a slow load continues. */
		slow?: boolean;
		/**
		 * Optional escape-hatch link to the support portal, shown in the slow hint
		 * and the error state. When omitted the form simply doesn't offer it, which
		 * keeps the fallback path removable (see `haloFallbackLink` in `#lib/config.js`).
		 */
		fallbackHref?: string;
		onload?: () => void;
		onerror?: () => void;
		onretry?: () => void;
	}

	const {
		status,
		src,
		height = null,
		slow = false,
		fallbackHref,
		onload,
		onerror,
		onretry
	}: Props = $props();
</script>

{#if status === 'error'}
	<div class="errorState" data-testid="support-error">
		<p>We couldn't load the support form.</p>
		{#if fallbackHref}
			<p>
				Please <a href={fallbackHref} target="_blank" rel="noopener">open the support portal</a>
				instead.
			</p>
		{/if}
		{#if onretry}
			<AbleyButton variant="outlined" onclick={onretry}>Try again</AbleyButton>
		{/if}
	</div>
{:else}
	<div class="formContainer">
		{#if status === 'loading'}
			<div class="loading" data-testid="support-loading">
				<LoadingSpinner text="Loading support form…" />
				{#if slow}
					<p class="slowHint" data-testid="support-slow-hint">
						Taking longer than expected.
						{#if fallbackHref}
							You can
							<a href={fallbackHref} target="_blank" rel="noopener">open the support portal</a>
							instead.
						{/if}
					</p>
				{/if}
			</div>
		{/if}
		<iframe
			title="Contact support form"
			{src}
			data-testid="support-iframe"
			class="formFrame"
			style:height={height ? `${height}px` : null}
			{onload}
			{onerror}></iframe>
	</div>
{/if}

<style>
	.formContainer {
		position: relative;
		width: 100%;
		min-height: 320px;
	}

	/* Fallback height until the iframe reports its own; capped so a tall form scrolls inside the iframe only. */
	.formFrame {
		border: none;
		border-radius: var(--radius);
		background-color: var(--surface);
		width: 100%;
		height: 60vh;
		min-height: 320px;
		max-height: calc(100vh - 16rem);
	}

	/* Opaque mask over the blank iframe while the Halo form loads. */
	.loading {
		display: flex;
		position: absolute;
		justify-content: center;
		align-items: center;
		inset: 0;
		background-color: var(--surface-raised);
		color: var(--text-muted);
	}

	.slowHint {
		position: absolute;
		right: var(--space-4);
		bottom: var(--space-5);
		left: var(--space-4);
		font-size: var(--font-size-sm);
		text-align: center;
	}

	.errorState {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-6);
	}
</style>
